import { ChangeDetectorRef, Component, Input, NgZone, OnDestroy, OnInit } from '@angular/core';
import { PluginListenerHandle } from '@capacitor/core';
import { ModalController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { HandleSubscriptionsComponent } from 'src/app/appDirectives/handle-subscriptions.directive';
import { RestaurantInfoResolver } from 'src/app/appResolvers/restaurant-info.resolver';
import { MyPosTerminalService } from 'src/app/appServices/mypos-terminal.service';
import { OrderService } from 'src/app/appServices/order.service';
import {
  MYPOS_APPROVAL_CODE_APPROVED,
  MYPOS_TERMINAL_STORAGE_KEYS,
  MyPosConnectionType,
} from 'src/app/core/constants/mypos-terminal.constants';
import { createSecurePaymentUuid } from 'src/app/core/payments/secure-payment-reference';
import { AutomaticPrintOutboxService } from 'src/app/core/printing/automatic-print-outbox.service';
import { StorageService } from 'src/app/core/services/storage.service';

type CompletionEndpoint =
  | 'createOrderByTerminal'
  | 'markTerminalOrderAsPaid'
  | 'createCardPaymentForOrderByTerminal';

interface ApprovedCompletionRequest {
  endpoint: CompletionEndpoint;
  gateway: 'MyPOS Card Reader';
  orderId: number | null;
  body: any;
}

@Component({
  selector: 'app-mypos-terminal',
  templateUrl: './mypos-terminal.component.html',
  styleUrls: ['./mypos-terminal.component.scss'],
  standalone: false,
})
export class MyposTerminalComponent
  extends HandleSubscriptionsComponent
  implements OnInit, OnDestroy
{
  @Input() dataPayload: any;

  basicInfo: any;
  readerStatus = 'Checking configured terminal...';
  paymentStatus = 'Pending';
  paymentDone = false;
  errorLog: string | null = null;
  loading = true;
  terminalPaymentActive = false;
  terminalCancelRequested = false;
  orderCreationActive = false;
  backendRetryAvailable = false;
  terminalRetryAvailable = false;
  manualReviewRequired = false;
  isClosing = false;
  printHandoffFailed = false;

  private statusListener?: PluginListenerHandle;
  private readonly terminalConnectMaxAttempts = 3;
  private readonly terminalConnectRetryDelayMs = 1500;
  private destroyed = false;
  private paymentReference: string | null = null;
  private orderIdempotencyKey: string | null = null;
  private approvedRequest: ApprovedCompletionRequest | null = null;

  constructor(
    private modalCtrl: ModalController,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
    private translate: TranslateService,
    private infoStorage: RestaurantInfoResolver,
    private myPosTerminalService: MyPosTerminalService,
    private orderService: OrderService,
    private storageService: StorageService,
    private automaticPrintOutbox: AutomaticPrintOutboxService,
  ) {
    super();
  }

  async ngOnInit(): Promise<void> {
    this.handleSubscription(this.infoStorage.restaurantBasicInfo, (res) => {
      if (res) {
        this.basicInfo = res;
      }
    });
    this.statusListener = await this.myPosTerminalService.addStatusListener(
      (event) => {
        this.ngZone.run(() => {
          if (event?.description) {
            this.readerStatus = event.description;
            this.detectChanges();
          }
        });
      },
    );
    void this.connectAndPay();
  }

  override ngOnDestroy(): void {
    this.destroyed = true;
    // Once an approval exists, never send a terminal cancellation. At that
    // point the only safe action is recording the exact approved request.
    if (
      this.terminalPaymentActive &&
      !this.paymentDone &&
      !this.approvedRequest &&
      !this.terminalCancelRequested
    ) {
      void this.myPosTerminalService.cancelPayment();
    }
    super.ngOnDestroy();
    void this.statusListener?.remove();
  }

  get hasApprovedUnsavedPayment(): boolean {
    return !!this.approvedRequest && !this.paymentDone;
  }

  get closeButtonText(): string {
    if (this.backendRetryAvailable) {
      return 'Retry saving payment';
    }
    if (this.terminalRetryAvailable) {
      return 'Try payment again';
    }
    if (this.orderCreationActive) {
      return 'Saving payment...';
    }
    if (this.terminalCancelRequested) {
      return 'Cancelling payment...';
    }
    if (this.terminalPaymentActive) {
      return 'Cancel payment';
    }
    return 'Close';
  }

  private async connectAndPay(): Promise<void> {
    if (
      this.terminalPaymentActive ||
      this.orderCreationActive ||
      this.approvedRequest
    ) {
      return;
    }
    this.loading = true;
    this.errorLog = null;
    this.terminalRetryAvailable = false;
    this.manualReviewRequired = false;
    this.readerStatus = 'Checking configured terminal...';
    this.paymentStatus = 'Pending';
    this.detectChanges();

    try {
      const configuredTerminal = await this.getConfiguredTerminal();
      if (!configuredTerminal.deviceAddress && !configuredTerminal.deviceName) {
        throw new Error(
          'No myPOS terminal is configured. Connect a terminal from Settings first.',
        );
      }
      const currency = this.myPosTerminalService.normalizeCurrency(
        this.basicInfo?.currencyCode,
      );
      const language = this.myPosTerminalService.normalizeLanguage(
        this.translate.currentLang,
      );
      const connectionType = configuredTerminal.connectionType || 'TCP_IP';
      const connection = await this.ensureTerminalConnected(
        configuredTerminal,
        currency,
        language,
      );

      this.readerStatus = connection.deviceName
        ? `Connected to ${connection.deviceName}.`
        : 'Terminal ready.';
      this.paymentStatus = 'Processing payment...';
      this.paymentReference =
        this.paymentReference ||
        this.myPosTerminalService.buildPaymentReference(
          this.dataPayload?.orderId || this.dataPayload?.id || 'cart',
        );
      this.detectChanges();

      this.terminalPaymentActive = true;
      const transaction = await this.myPosTerminalService.pay(
        Number(this.dataPayload.totalAmount),
        {
          currency,
          language,
          connectionType,
          reference: this.paymentReference,
        },
      );
      this.terminalPaymentActive = false;

      const validatedTransaction = this.validateApprovedTransaction(
        transaction,
        currency,
        this.paymentReference,
      );
      this.approvedRequest = this.buildCompletionRequest(
        this.buildTerminalPayload(validatedTransaction, connectionType),
      );
      await this.submitApprovedRequest();
    } catch (error: any) {
      this.terminalPaymentActive = false;
      this.orderCreationActive = false;
      if (this.terminalCancelRequested && !this.approvedRequest) {
        this.closeCardReaderModel(null);
        return;
      }
      const message =
        error?.message ||
        error?.toString?.() ||
        'Unable to complete myPOS payment.';
      const nativeCode = String(error?.code || '').trim().toUpperCase();
      const recoverableOutcome =
        this.classifyRecoverableTerminalOutcome(nativeCode, message);
      this.manualReviewRequired =
        nativeCode === 'MYPOS_OUTCOME_UNKNOWN' ||
        String(message).includes('MYPOS_OUTCOME_UNKNOWN:');
      this.terminalRetryAvailable =
        !this.manualReviewRequired && recoverableOutcome !== null;
      if (this.manualReviewRequired) {
        this.errorLog = String(message)
          .replace('MYPOS_OUTCOME_UNKNOWN:', '')
          .trim();
        this.paymentStatus =
          'Payment outcome unknown — staff review required';
      } else if (recoverableOutcome === 'cancelled') {
        this.errorLog =
          'The payment was cancelled. No order was created and you can try again.';
        this.paymentStatus = 'Payment cancelled';
      } else if (recoverableOutcome === 'declined') {
        this.errorLog =
          nativeCode === 'MYPOS_DECLINED'
            ? 'The payment was declined (for example, insufficient funds). No order was created. Use another card or try again.'
            : 'The terminal could not complete the payment. No order was created and you can try again.';
        this.paymentStatus =
          nativeCode === 'MYPOS_DECLINED'
            ? 'Payment declined'
            : 'Payment not completed';
      } else {
        this.errorLog = message;
        this.paymentStatus = 'Payment failed';
      }
      this.loading = false;
      this.detectChanges();
    }
  }

  private async submitApprovedRequest(): Promise<void> {
    const request = this.approvedRequest;
    if (!request || this.orderCreationActive || this.paymentDone) {
      return;
    }
    this.orderCreationActive = true;
    this.backendRetryAvailable = false;
    this.errorLog = null;
    this.paymentStatus = 'Saving approved payment...';
    this.loading = true;
    this.detectChanges();

    try {
      let response: any;
      switch (request.endpoint) {
        case 'markTerminalOrderAsPaid':
          response = await firstValueFrom(
            this.orderService.markTerminalOrderAsPaid(
              request.orderId,
              request.gateway,
              request.body,
            ),
          );
          break;
        case 'createCardPaymentForOrderByTerminal':
          response = await firstValueFrom(
            this.orderService.createCardPaymentForOrderByTerminal(
              request.orderId,
              request.gateway,
              request.body,
            ),
          );
          break;
        case 'createOrderByTerminal':
        default:
          response = await firstValueFrom(
            this.orderService.createOrderByTerminal(
              request.gateway,
              request.body,
            ),
          );
          break;
      }
      await this.finishSuccessfulRequest(request, response);
    } catch (error: any) {
      // Keep the immutable request. The button retries only this backend call;
      // it never invokes the terminal for a second debit.
      this.orderCreationActive = false;
      this.backendRetryAvailable = true;
      this.loading = false;
      this.paymentStatus = 'Payment approved — order not saved yet';
      this.errorLog =
        error?.error?.message ||
        'myPOS approved the payment, but the POS could not save it. Do not charge again. Restore the connection and press “Retry saving payment”.';
      this.detectChanges();
    }
  }

  private async finishSuccessfulRequest(
    request: ApprovedCompletionRequest,
    response: any,
  ): Promise<void> {
    let orderId: number | null = request.orderId;
    if (request.endpoint === 'createOrderByTerminal') {
      orderId = Number(response);
      if (!Number.isInteger(orderId) || Number(orderId) <= 0) {
        throw new Error(
          'The backend did not return the created order id. Retry saving this approved payment.',
        );
      }
    }

    try {
      if (request.endpoint === 'createOrderByTerminal' && orderId) {
        const isParent =
          Array.isArray(this.dataPayload?.orderIdList) &&
          this.dataPayload.orderIdList.length > 0;
        if (!isParent) {
          await this.automaticPrintOutbox.planKOT(orderId);
        }
        await this.automaticPrintOutbox.planBilling(orderId);
      } else if (
        request.endpoint === 'markTerminalOrderAsPaid' &&
        orderId
      ) {
        const isKiosk =
          String(this.dataPayload?.channel || '').trim().toLowerCase() ===
          'kiosk';
        if (isKiosk) {
          await this.automaticPrintOutbox.planKOT(orderId);
        } else {
          await this.automaticPrintOutbox.planBilling(orderId);
        }
      }
    } catch (error) {
      // The durable outbox reports its own retry state. Payment recording is
      // already confirmed and must never be repeated because printing failed.
      this.printHandoffFailed = true;
      console.error('myPOS payment saved but print handoff failed', error);
    }

    this.orderCreationActive = false;
    this.backendRetryAvailable = false;
    this.paymentDone = true;
    this.loading = false;
    this.paymentStatus = 'Payment is done successfully.';
    this.approvedRequest = null;

    if (request.endpoint === 'markTerminalOrderAsPaid') {
      this.dataPayload.paid = true;
      this.dataPayload.paymentType = 'card';
      this.closeCardReaderModel(this.dataPayload);
      return;
    }
    if (request.endpoint === 'createCardPaymentForOrderByTerminal') {
      this.closeCardReaderModel(this.dataPayload);
      return;
    }
    this.closeCardReaderModel({
      id: orderId,
      paymentType: 'Card Reader',
      printHandoffFailed: this.printHandoffFailed,
    });
  }

  private validateApprovedTransaction(
    transaction: any,
    expectedCurrency: string,
    expectedReference: string,
  ): any {
    const approvalCode = String(transaction?.approval || '').trim();
    const providerReference =
      transaction?.rrn || transaction?.stan || transaction?.authCode;
    if (
      approvalCode !== MYPOS_APPROVAL_CODE_APPROVED ||
      !String(providerReference || '').trim()
    ) {
      throw new Error(
        approvalCode !== MYPOS_APPROVAL_CODE_APPROVED
          ? `Payment was not approved by the myPOS terminal (code ${approvalCode || 'unknown'}).`
          : 'The terminal did not return a transaction reference. Check its receipt before trying again.',
      );
    }

    const expectedCents = this.toCents(this.dataPayload?.totalAmount);
    const providerCents = this.toCents(transaction?.amount);
    const providerCurrency = this.normalizeProviderCurrency(
      transaction?.currencyIsoCode,
    );
    if (
      expectedCents === null ||
      providerCents === null ||
      expectedCents !== providerCents ||
      providerCurrency !== expectedCurrency
    ) {
      throw new Error(
        `The terminal result does not match this sale (${transaction?.amount || '?'} ${providerCurrency || '?'} instead of ${Number(this.dataPayload?.totalAmount).toFixed(2)} ${expectedCurrency}). No order was created.`,
      );
    }

    if (
      transaction?.tranRef &&
      !this.referencesMatch(expectedReference, transaction.tranRef)
    ) {
      throw new Error(
        'The terminal returned a result belonging to another payment. No order was created.',
      );
    }
    return {
      ...transaction,
      transactionId: String(providerReference).trim(),
      providerCurrency,
    };
  }

  private buildTerminalPayload(
    transaction: any,
    connectionType: MyPosConnectionType,
  ): any {
    const providerReference =
      transaction?.rrn || transaction?.stan || transaction?.authCode;
    const providerReferenceType = transaction?.rrn
      ? 'rrn'
      : transaction?.stan
        ? 'stan'
        : 'authCode';
    const providerAmount = Number(this.dataPayload.totalAmount).toFixed(2);
    return {
      paymentVendorId: String(providerReference).trim(),
      deviceId: transaction?.terminalId || null,
      cardInfo: {
        transactionId: String(providerReference).trim(),
        cardholderName: transaction?.embossName || null,
        last4: this.myPosTerminalService.extractLast4(transaction?.panMasked),
        network: transaction?.aidName || null,
        readMethod: connectionType.toLowerCase(),
        providerAmount,
        providerCurrencyIsoCode: transaction.providerCurrency,
        providerTransactionDateLocal:
          transaction?.transactionDateLocal || null,
        providerReferenceType,
        providerApprovalCode: MYPOS_APPROVAL_CODE_APPROVED,
      },
    };
  }

  private buildCompletionRequest(
    terminalPayload: any,
  ): ApprovedCompletionRequest {
    const gateway = 'MyPOS Card Reader' as const;
    switch (this.dataPayload.type) {
      case 'pay by orderId':
        return this.snapshotRequest({
          endpoint: 'markTerminalOrderAsPaid',
          gateway,
          orderId: Number(this.dataPayload.id),
          body: {
            totalAmount: this.dataPayload.totalAmount,
            processingFee: this.dataPayload.processingFee || 0,
            ...terminalPayload,
          },
        });
      case 'pay by paymentIntent':
        return this.snapshotRequest({
          endpoint: 'createCardPaymentForOrderByTerminal',
          gateway,
          orderId: Number(this.dataPayload.orderId),
          body: {
            amount: this.dataPayload.totalAmount,
            paymentType: 'card',
            processingFee: this.dataPayload.processingFee || 0,
            ...terminalPayload,
          },
        });
      case 'pay by order':
      default:
        this.orderIdempotencyKey =
          this.orderIdempotencyKey || createSecurePaymentUuid();
        return this.snapshotRequest({
          endpoint: 'createOrderByTerminal',
          gateway,
          orderId: null,
          body: {
            ...this.dataPayload,
            ...terminalPayload,
            uuid: this.orderIdempotencyKey,
            paymentType: 'card',
          },
        });
    }
  }

  private snapshotRequest(
    request: ApprovedCompletionRequest,
  ): ApprovedCompletionRequest {
    return JSON.parse(JSON.stringify(request));
  }

  async handleCloseRequest(): Promise<void> {
    if (this.isClosing || this.paymentDone) {
      return;
    }
    if (this.backendRetryAvailable && this.approvedRequest) {
      await this.submitApprovedRequest();
      return;
    }
    if (this.orderCreationActive || this.approvedRequest) {
      this.errorLog =
        'Payment was accepted. Please wait while the POS saves it.';
      this.detectChanges();
      return;
    }
    if (this.terminalPaymentActive) {
      await this.cancelTerminalPayment();
      return;
    }
    this.closeCardReaderModel(null);
  }

  async handlePrimaryAction(): Promise<void> {
    if (this.terminalRetryAvailable) {
      await this.retryTerminalPayment();
      return;
    }
    await this.handleCloseRequest();
  }

  private async retryTerminalPayment(): Promise<void> {
    if (
      !this.terminalRetryAvailable ||
      this.terminalPaymentActive ||
      this.orderCreationActive ||
      this.approvedRequest
    ) {
      return;
    }
    // A cancelled/declined terminal attempt is finished. The next explicit
    // user action uses a fresh terminal reference; this is never an automatic
    // replay of the previous purchase.
    this.terminalRetryAvailable = false;
    this.terminalCancelRequested = false;
    this.paymentReference = null;
    await this.connectAndPay();
  }

  private classifyRecoverableTerminalOutcome(
    nativeCode: string,
    message: string,
  ): 'cancelled' | 'declined' | null {
    if (
      nativeCode === 'MYPOS_CANCELLED' ||
      String(message).includes('MYPOS_CANCELLED:')
    ) {
      return 'cancelled';
    }
    if (
      nativeCode === 'MYPOS_DECLINED' ||
      nativeCode === 'MYPOS_TERMINAL_FAILED' ||
      String(message).includes('MYPOS_DECLINED:') ||
      String(message).includes('MYPOS_TERMINAL_FAILED:')
    ) {
      return 'declined';
    }
    return null;
  }

  private async cancelTerminalPayment(): Promise<void> {
    if (this.terminalCancelRequested) {
      return;
    }
    this.terminalCancelRequested = true;
    this.errorLog = null;
    this.paymentStatus = 'Cancelling payment...';
    this.readerStatus = 'Cancelling payment on myPOS terminal...';
    this.detectChanges();
    try {
      await this.myPosTerminalService.cancelPayment();
      setTimeout(() => {
        if (
          !this.destroyed &&
          this.terminalPaymentActive &&
          this.terminalCancelRequested &&
          !this.paymentDone
        ) {
          this.errorLog =
            'Cancellation was sent. If the terminal still asks for payment, cancel it on the terminal.';
          this.detectChanges();
        }
      }, 8000);
    } catch (error: any) {
      this.terminalCancelRequested = false;
      this.errorLog =
        error?.message ||
        'Unable to cancel from the POS. Cancel directly on the terminal.';
      this.paymentStatus = 'Payment cancellation failed';
      this.detectChanges();
    }
  }

  private async ensureTerminalConnected(
    configuredTerminal: {
      deviceName: string | null;
      deviceAddress: string | null;
      connectionType: MyPosConnectionType;
      tcpIpAddress: string | null;
      tcpIpPort: number | null;
    },
    currency: string,
    language: string,
  ): Promise<any> {
    let lastError: any;
    for (
      let attempt = 1;
      attempt <= this.terminalConnectMaxAttempts;
      attempt += 1
    ) {
      if (this.destroyed || this.terminalCancelRequested) {
        throw new Error('myPOS payment was cancelled.');
      }
      const currentConnection = await this.myPosTerminalService.isConnected();
      if (
        this.isConfiguredTerminalReady(
          currentConnection,
          configuredTerminal.deviceAddress,
        )
      ) {
        return currentConnection;
      }
      this.readerStatus =
        attempt === 1
          ? 'Connecting configured myPOS terminal...'
          : `Reconnecting configured myPOS terminal (${attempt}/${this.terminalConnectMaxAttempts})...`;
      this.detectChanges();
      try {
        const connection = await this.myPosTerminalService.connect({
          currency,
          language,
          connectionType: configuredTerminal.connectionType,
          deviceAddress:
            configuredTerminal.connectionType === 'BLUETOOTH'
              ? configuredTerminal.deviceAddress || undefined
              : undefined,
          tcpIpAddress:
            configuredTerminal.connectionType === 'TCP_IP'
              ? configuredTerminal.tcpIpAddress || undefined
              : undefined,
          tcpIpPort:
            configuredTerminal.connectionType === 'TCP_IP'
              ? Number(configuredTerminal.tcpIpPort)
              : undefined,
        });
        if (
          this.isConfiguredTerminalReady(
            connection,
            configuredTerminal.deviceAddress,
          )
        ) {
          return connection;
        }
        lastError = new Error(
          'The configured myPOS terminal is not ready yet.',
        );
      } catch (error) {
        lastError = error;
      }
      if (attempt < this.terminalConnectMaxAttempts) {
        await new Promise((resolve) =>
          setTimeout(resolve, this.terminalConnectRetryDelayMs),
        );
      }
    }
    throw (
      lastError ||
      new Error('Unable to connect the configured myPOS terminal.')
    );
  }

  private isConfiguredTerminalReady(
    connection: any,
    configuredAddress: string | null,
  ): boolean {
    return (
      !!connection?.connected &&
      !!connection?.ready &&
      (
        !configuredAddress ||
        !connection.deviceAddress ||
        configuredAddress === connection.deviceAddress
      )
    );
  }

  private async getConfiguredTerminal(): Promise<{
    deviceName: string | null;
    deviceAddress: string | null;
    connectionType: MyPosConnectionType;
    tcpIpAddress: string | null;
    tcpIpPort: number | null;
  }> {
    const deviceName = await this.storageService.get(
      MYPOS_TERMINAL_STORAGE_KEYS.NAME,
    );
    const deviceAddress = await this.storageService.get(
      MYPOS_TERMINAL_STORAGE_KEYS.ADDRESS,
    );
    const connectionType =
      (
        await this.storageService.get(
          MYPOS_TERMINAL_STORAGE_KEYS.CONNECTION_TYPE,
        )
      ) as MyPosConnectionType | null;
    const resolvedConnectionType = connectionType || 'TCP_IP';
    const tcpIpAddress = await this.storageService.get(
      MYPOS_TERMINAL_STORAGE_KEYS.TCP_IP_ADDRESS,
    );
    const tcpIpPortValue = await this.storageService.get(
      MYPOS_TERMINAL_STORAGE_KEYS.TCP_IP_PORT,
    );
    const tcpIpPort = tcpIpPortValue ? Number(tcpIpPortValue) : null;
    return {
      deviceName,
      deviceAddress:
        deviceAddress ||
        (
          resolvedConnectionType === 'TCP_IP' && tcpIpAddress
            ? `${tcpIpAddress}:${tcpIpPort || ''}`
            : null
        ),
      connectionType: resolvedConnectionType,
      tcpIpAddress,
      tcpIpPort,
    };
  }

  private toCents(value: unknown): number | null {
    const normalized = String(value ?? '').trim().replace(',', '.');
    if (!/^\d+(?:\.\d{1,2})?$/.test(normalized)) {
      return null;
    }
    const amount = Number(normalized);
    return Number.isFinite(amount) ? Math.round(amount * 100) : null;
  }

  private normalizeProviderCurrency(value: unknown): string | null {
    const normalized = String(value ?? '').trim().toUpperCase();
    const numeric: Record<string, string> = {
      '756': 'CHF',
      '978': 'EUR',
      '840': 'USD',
      '826': 'GBP',
      '203': 'CZK',
      '946': 'RON',
      '348': 'HUF',
      '975': 'BGN',
      '985': 'PLN',
    };
    if (numeric[normalized]) {
      return numeric[normalized];
    }
    return /^[A-Z]{3}$/.test(normalized) ? normalized : null;
  }

  private referencesMatch(expected: unknown, actual: unknown): boolean {
    const left = String(expected ?? '')
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, '');
    const right = String(actual ?? '')
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, '');
    if (!left || !right) {
      return false;
    }
    if (left === right) {
      return true;
    }
    return (
      Math.min(left.length, right.length) >= 12 &&
      (left.startsWith(right) || right.startsWith(left))
    );
  }

  private detectChanges(): void {
    if (!this.destroyed) {
      this.cdr.detectChanges();
    }
  }

  closeCardReaderModel(response: any): void {
    if (this.isClosing) {
      return;
    }
    this.isClosing = true;
    void this.modalCtrl.dismiss(response);
  }
}
